import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  PLAN_LABEL,
  PLAN_LIMIT_KEYS,
  PLAN_NAMES,
  PLAN_SOURCE_LABEL,
} from '../../components/plan/planLabels';
import { getErrorMessage } from '../../lib/errors';
import { formatDate } from '../../lib/format';
import type {
  AdminAccountPlanUpdate,
  AdminAccountRead,
  AdminPlanReconcile,
  PlanName,
  TrialState,
} from '../../types/api';
import { billingErrorMessage } from '../billing/billingApi';
import { listAccounts, setAccountPlan } from './adminApi';
import { AdminPlanStats } from './AdminPlanStats';

/** The inline editor's fields, as typed. Converted to the wire body on save. */
interface PlanDraft {
  plan: PlanName;
  /** `YYYY-MM-DD`, or empty for no expiry. */
  expiresOn: string;
  note: string;
  /** Raw JSON. Empty = no custom limits (the PUT replaces them). */
  override: string;
}

type OverrideParse =
  { ok: true; value: Record<string, number | null> | null } | { ok: false; error: string };

function isPlanName(value: string): value is PlanName {
  return (PLAN_NAMES as readonly string[]).includes(value);
}

/** The UTC calendar day of an instant, for a date input. */
function dayOf(value: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

/** A picked day runs to its last second, UTC: "until 20 Oct" includes the 20th. */
function endOfDay(day: string): string {
  return `${day}T23:59:59Z`;
}

function draftFor(account: AdminAccountRead): PlanDraft {
  return {
    plan: account.plan,
    expiresOn: dayOf(account.plan_expires_at),
    note: account.plan_note ?? '',
    override: account.plan_limits_override ? JSON.stringify(account.plan_limits_override) : '',
  };
}

/**
 * Reads the override textarea. The PUT replaces the overrides, so the box is
 * pre-filled with the current value and empty (or `null`) clears them;
 * otherwise a JSON object of known limit keys, each a whole number zero or
 * more, or null for unlimited.
 */
function parseOverride(text: string): OverrideParse {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'The override is not valid JSON.' };
  }
  if (parsed === null) return { ok: true, value: null };
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'The override must be a JSON object, or null to clear it.' };
  }
  const result: Record<string, number | null> = {};
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!(PLAN_LIMIT_KEYS as readonly string[]).includes(key)) {
      return {
        ok: false,
        error: `"${key}" is not a limit. Use: ${PLAN_LIMIT_KEYS.join(', ')}.`,
      };
    }
    if (value !== null && !(typeof value === 'number' && Number.isInteger(value) && value >= 0)) {
      return {
        ok: false,
        error: `"${key}" must be a whole number, zero or more, or null for unlimited.`,
      };
    }
    result[key] = value;
  }
  return { ok: true, value: result };
}

function accountName(account: AdminAccountRead): string {
  return account.email ?? `Account ${account.user_id.slice(0, 8)}`;
}

const TRIAL_LABEL: Readonly<Record<TrialState, string>> = {
  active: 'Active',
  ended: 'Ended',
};

/** "1 member", "2 members". */
function count(value: number, one: string, many: string): string {
  return `${value} ${value === 1 ? one : many}`;
}

/**
 * The side effects of a plan save, non-zero counts only: "Suspended 1
 * member, parked 2 autopilot plans." Null when the save moved nothing else.
 */
function describeReconcile(reconcile: AdminPlanReconcile): string | null {
  const parts: string[] = [];
  if (reconcile.suspended > 0) {
    parts.push(`suspended ${count(reconcile.suspended, 'member', 'members')}`);
  }
  if (reconcile.reactivated > 0) {
    parts.push(`reactivated ${count(reconcile.reactivated, 'member', 'members')}`);
  }
  if (reconcile.invites_removed > 0) {
    parts.push(`removed ${count(reconcile.invites_removed, 'invite', 'invites')}`);
  }
  if (reconcile.autopilot_parked > 0) {
    parts.push(`parked ${count(reconcile.autopilot_parked, 'autopilot plan', 'autopilot plans')}`);
  }
  if (reconcile.news_disabled > 0) {
    parts.push(
      `turned off the daily news post on ${count(reconcile.news_disabled, 'brand', 'brands')}`,
    );
  }
  if (parts.length === 0) return null;
  const sentence = parts.join(', ');
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

/** What the confirmation says the save will do, in one sentence. */
function describeChange(account: AdminAccountRead, body: AdminAccountPlanUpdate): string {
  const until = body.plan_expires_at
    ? `until ${formatDate(dayOf(body.plan_expires_at))} (UTC)`
    : 'with no end date';
  let sentence = `${accountName(account)} goes on ${PLAN_LABEL[body.plan]}, set by Autopostmate, ${until}.`;
  if (body.plan_limits_override === null) {
    if (account.plan_limits_override) sentence += ' Custom limits are cleared.';
  } else {
    sentence += ` Custom limits: ${JSON.stringify(body.plan_limits_override)}.`;
  }
  if (account.paddle_subscription_id) {
    sentence += body.plan_note?.startsWith('pin:')
      ? ' This account has a Paddle subscription; the pin keeps this plan over its updates.'
      : ' This account has a Paddle subscription; without a note starting "pin:" the save is refused.';
  }
  return `${sentence} The change is recorded against your account.`;
}

/**
 * Every account with its plan, and a by-hand plan change per row: plan,
 * end date, a staff note, and custom limits as JSON. The source is always
 * `manual`; trials and Paddle set their own.
 */
export function AdminAccounts() {
  const [accounts, setAccounts] = useState<AdminAccountRead[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState<AdminAccountPlanUpdate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Bumped after a plan save so the stats strip refetches its counts.
  const [statsKey, setStatsKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const rows = await listAccounts(controller.signal);
        if (cancelled) return;
        setAccounts(rows);
        setLoadError(null);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoadError(getErrorMessage(caught, 'Could not load the accounts.'));
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [nonce]);

  const reload = useCallback(() => {
    setLoadError(null);
    setNonce((value) => value + 1);
  }, []);

  const editingAccount = accounts?.find((account) => account.user_id === editing) ?? null;

  function open(account: AdminAccountRead) {
    setEditing(account.user_id);
    setDraft(draftFor(account));
    setFormError(null);
    setSaveError(null);
    setNotice(null);
  }

  function close() {
    setEditing(null);
    setDraft(null);
    setFormError(null);
  }

  function askToSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (draft === null) return;
    const override = parseOverride(draft.override);
    if (!override.ok) {
      setFormError(override.error);
      return;
    }
    const note = draft.note.trim();
    const body: AdminAccountPlanUpdate = {
      plan: draft.plan,
      plan_source: 'manual',
      plan_expires_at: draft.expiresOn ? endOfDay(draft.expiresOn) : null,
      plan_note: note === '' ? null : note,
      plan_limits_override: override.value,
    };
    setFormError(null);
    setSaveError(null);
    setPending(body);
  }

  function commit() {
    if (pending === null || editing === null || isSaving) return;
    const userId = editing;
    setIsSaving(true);
    setSaveError(null);

    void (async () => {
      try {
        const { reconcile, ...saved } = await setAccountPlan(userId, pending);
        setAccounts(
          (rows) => rows?.map((row) => (row.user_id === saved.user_id ? saved : row)) ?? rows,
        );
        // Older backends sent no reconcile; the save line stands alone then.
        const moved = reconcile ? describeReconcile(reconcile) : null;
        setNotice(
          `Saved. ${accountName(saved)} is on ${PLAN_LABEL[saved.plan]}.${moved ? ` ${moved}` : ''}`,
        );
        setStatsKey((value) => value + 1);
        setPending(null);
        setIsSaving(false);
        close();
      } catch (caught) {
        setIsSaving(false);
        setSaveError(billingErrorMessage(caught, 'That change did not save. Try again.'));
      }
    })();
  }

  if (loadError !== null) {
    return (
      <div className="admin-pane">
        <AdminPlanStats refreshKey={statsKey} />
        <div className="admin-pane__foot">
          <p className="form__error" role="alert">
            {loadError}
          </p>
          <button className="button" type="button" onClick={reload}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (accounts === null) {
    return (
      <div className="admin-pane">
        <AdminPlanStats refreshKey={statsKey} />
        <p className="kb-section__status" role="status">
          Loading accounts
        </p>
      </div>
    );
  }

  return (
    <div className="admin-pane">
      <AdminPlanStats refreshKey={statsKey} />

      {notice ? (
        <p className="form__notice" role="status">
          {notice}
        </p>
      ) : null}

      {accounts.length === 0 ? (
        <p className="admin-empty">No accounts on this deployment yet.</p>
      ) : (
        <div className="learning-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col">Plan</th>
                <th scope="col">Source</th>
                <th scope="col">Trial</th>
                <th scope="col">Expires</th>
                <th scope="col">Note</th>
                <th scope="col">Brands</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {accounts.flatMap((account) => {
                const isOpen = account.user_id === editing;
                const row = (
                  <tr
                    key={account.user_id}
                    className={isOpen ? 'admin-table__row--open' : undefined}
                  >
                    <td>{accountName(account)}</td>
                    <td>{PLAN_LABEL[account.plan]}</td>
                    <td>
                      {PLAN_SOURCE_LABEL[account.plan_source]}
                      {account.paddle_subscription_id ? (
                        <span className="admin-mark" title={account.paddle_subscription_id}>
                          sub
                        </span>
                      ) : null}
                    </td>
                    <td>{account.trial_state ? TRIAL_LABEL[account.trial_state] : '—'}</td>
                    <td className="admin-table__number">
                      {account.plan_expires_at ? formatDate(dayOf(account.plan_expires_at)) : '—'}
                    </td>
                    <td>{account.plan_note ?? ''}</td>
                    <td className="admin-table__number">{account.business_count}</td>
                    <td>
                      {isOpen ? null : (
                        <button
                          className="admin-table__open"
                          type="button"
                          onClick={() => open(account)}
                        >
                          Edit plan
                        </button>
                      )}
                    </td>
                  </tr>
                );
                if (!isOpen || draft === null) return [row];
                return [
                  row,
                  <tr key={`${account.user_id}-editor`} className="admin-table__row--open">
                    <td colSpan={8}>
                      <AccountPlanForm
                        account={account}
                        draft={draft}
                        error={formError}
                        isSaving={isSaving}
                        onChange={setDraft}
                        onSubmit={askToSave}
                        onCancel={close}
                      />
                    </td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {pending && editingAccount ? (
        <ConfirmDialog
          title="Change this plan"
          message={describeChange(editingAccount, pending)}
          confirmLabel="Set plan"
          busyLabel="Saving"
          dismissLabel="Cancel"
          isBusy={isSaving}
          error={saveError}
          onConfirm={commit}
          onDismiss={() => setPending(null)}
        />
      ) : null}
    </div>
  );
}

function AccountPlanForm({
  account,
  draft,
  error,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}: {
  account: AdminAccountRead;
  draft: PlanDraft;
  error: string | null;
  isSaving: boolean;
  onChange: (draft: PlanDraft) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
}) {
  const idBase = `account-plan-${account.user_id}`;

  return (
    <form className="admin-form admin-form--plan" onSubmit={onSubmit} noValidate>
      <label className="field" htmlFor={`${idBase}-plan`}>
        <span className="field__label">Plan</span>
        <select
          className="field__input"
          id={`${idBase}-plan`}
          value={draft.plan}
          disabled={isSaving}
          onChange={(event) => {
            if (isPlanName(event.target.value)) onChange({ ...draft, plan: event.target.value });
          }}
        >
          {PLAN_NAMES.map((plan) => (
            <option key={plan} value={plan}>
              {PLAN_LABEL[plan]}
            </option>
          ))}
        </select>
        <span className="field__hint">Saved as a manual plan, whatever the source was.</span>
      </label>

      <label className="field" htmlFor={`${idBase}-expires`}>
        <span className="field__label">Ends on</span>
        <input
          className="field__input"
          id={`${idBase}-expires`}
          type="date"
          value={draft.expiresOn}
          disabled={isSaving}
          onChange={(event) => onChange({ ...draft, expiresOn: event.target.value })}
        />
        <span className="field__hint">
          Optional. Runs to the end of that day, UTC. Empty means no end date.
        </span>
      </label>

      <label className="field" htmlFor={`${idBase}-note`}>
        <span className="field__label">Note</span>
        <input
          className="field__input"
          id={`${idBase}-note`}
          type="text"
          value={draft.note}
          disabled={isSaving}
          placeholder="design partner"
          onChange={(event) => onChange({ ...draft, note: event.target.value })}
        />
      </label>

      <label className="field" htmlFor={`${idBase}-override`}>
        <span className="field__label">Custom limits (JSON)</span>
        <textarea
          className="field__input admin-form__json"
          id={`${idBase}-override`}
          rows={4}
          spellCheck={false}
          value={draft.override}
          disabled={isSaving}
          placeholder='{"posts": 500, "brands": 10, "team_members": null}'
          onChange={(event) => onChange({ ...draft, override: event.target.value })}
        />
        <span className="field__hint">
          Agency only. Empty clears custom limits. A value of null is unlimited. Keys:{' '}
          {PLAN_LIMIT_KEYS.join(', ')}.
        </span>
      </label>

      {account.paddle_customer_id ? (
        <p className="field__hint">
          Paddle customer {account.paddle_customer_id}
          {account.paddle_subscription_id ? `, subscription ${account.paddle_subscription_id}` : ''}
          .
        </p>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="admin-panel__actions">
        <button className="button button--primary" type="submit" disabled={isSaving}>
          Save plan
        </button>
        <button className="button" type="button" disabled={isSaving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
